import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import './DadataSuggest.css';

export default function DadataSuggest({
    value,
    onChange,
    onSelect,
    endpoint,
    findEndpoint,   // если задан — вызывается при minLength символах (для ИНН)
    minLength = 2,
    placeholder,
    disabled,
    renderItem,
}) {
    const { request } = useApi();
    const [query, setQuery] = useState(value || '');
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [activeIdx, setActiveIdx] = useState(-1);
    const timerRef = useRef(null);
    const wrapRef = useRef(null);

    useEffect(() => { setQuery(value || ''); }, [value]);

    useEffect(() => {
        const onOut = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', onOut);
        return () => document.removeEventListener('mousedown', onOut);
    }, []);

    const callEndpoint = useCallback(async (url, q) => {
        const res = await request(url, { method: 'POST', body: JSON.stringify({ query: q, count: 7 }) });
        return res?.suggestions || [];
    }, [request]);

    const fetchSuggestions = useCallback(async (q) => {
        if (!q || q.length < minLength) { setSuggestions([]); setOpen(false); return; }
        setLoading(true);
        setError('');
        try {
            // findEndpoint: точный поиск по ИНН когда длина достигла нужного значения
            const url = (findEndpoint && q.length >= minLength) ? findEndpoint : endpoint;
            const items = await callEndpoint(url, q);
            setSuggestions(items);
            setOpen(items.length > 0);
            setActiveIdx(-1);
        } catch (e) {
            console.error('[DadataSuggest] error:', e?.message);
            setError(e?.message || 'Ошибка подсказок');
            setSuggestions([]);
        } finally {
            setLoading(false);
        }
    }, [request, endpoint, findEndpoint, minLength, callEndpoint]);

    const handleChange = e => {
        const v = e.target.value;
        setQuery(v);
        setError('');
        onChange?.(v);
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => fetchSuggestions(v), 350);
    };

    const handleSelect = s => {
        setQuery(s.value);
        setSuggestions([]);
        setOpen(false);
        onChange?.(s.value);
        onSelect?.(s);
    };

    const handleKeyDown = e => {
        if (!open || !suggestions.length) return;
        if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, suggestions.length - 1)); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
        else if (e.key === 'Enter' && activeIdx >= 0) { e.preventDefault(); handleSelect(suggestions[activeIdx]); }
        else if (e.key === 'Escape') setOpen(false);
    };

    return (
        <div className="dd-wrap" ref={wrapRef}>
            <div className="dd-input-row">
                <input
                    type="text"
                    className={`dd-input${error ? ' dd-input--error' : ''}`}
                    value={query}
                    onChange={handleChange}
                    onFocus={() => suggestions.length && setOpen(true)}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    disabled={disabled}
                    autoComplete="off"
                />
                {loading && <span className="dd-spinner" />}
            </div>
            {error && <span className="dd-error">{error}</span>}
            {open && suggestions.length > 0 && (
                <ul className="dd-list">
                    {suggestions.map((s, i) => (
                        <li
                            key={i}
                            className={`dd-item${i === activeIdx ? ' dd-item--active' : ''}`}
                            onMouseDown={() => handleSelect(s)}
                        >
                            {renderItem ? renderItem(s) : s.value}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
